/**
 * What the progress popup can tell from a run's steps and its error
 * (ZLAYT-1642, after ZLAYT-1641 on the server).
 *
 * The server sends no flag for a wait. While a report file waits for the
 * upload read, the Extracting Text step's line says so in words
 * (use_case/tasks/instance.py, reading_progress_line): "Read 0 of 3 files.
 * Waiting for 1 file still being read after upload; large files can take a
 * few minutes". Those words are the signal, as pipelines.ts mirrors the
 * server's step list. The percent does not move during the wait, so the
 * popup says "Waiting" instead of a number that looks stuck.
 */
const UPLOAD_WAIT = /\bwaiting for .+ still being read after upload\b/i;

export interface RunTaskLike {
  status: string;
  current_step?: string | null;
}

/** The step line of a running step that waits on an upload read, or null. */
export function uploadWaitText(tasks: RunTaskLike[]): string | null {
  const waiting = tasks.find(
    (task) =>
      task.status === 'IN_PROGRESS' &&
      UPLOAD_WAIT.test(String(task.current_step || ''))
  );
  return waiting ? String(waiting.current_step).trim() : null;
}

/**
 * How long the overall percent may sit still while a run generates before
 * the popup says it is still working (ZLAYT-1642). Engine-independent: the
 * demand letter engine (document_generator) sends no wait wording, and its
 * snapshot moves only when a whole step finishes.
 */
export const STALL_AFTER_MS = 45_000;

// A message that says only what the heading already says ("Generation
// failed"): the demand letter engine's default user message.
const SAYS_NOTHING = /^(document )?generation failed\.?$/i;

const stripPrefix = (text: string) => {
  const plain = text.trim().replace(/^generation failed:\s*/i, '');
  return SAYS_NOTHING.test(plain) ? '' : plain;
};

/**
 * The plain reason a run failed, from what the progress hook stores as its
 * error: the socket status payload ({status, message}), the progress
 * snapshot ({status, message, error}) or nothing useful (the generation
 * status poll and the demand letter snapshot carry no reason). Empty when
 * there is none, or when the message only repeats the heading.
 */
export function failureReasonText(error: unknown): string {
  if (typeof error === 'string') return stripPrefix(error);
  if (!error || typeof error !== 'object') return '';
  const body = error as { message?: unknown; error?: unknown };
  const found = [body.message, body.error].find(
    (value) => typeof value === 'string' && value.trim()
  );
  return typeof found === 'string' ? stripPrefix(found) : '';
}
